/**
 * HtmlField — rich text HTML editor backed by Lexical.
 *
 * Used for the `x-ms-editor: html` parameter pattern (Outlook/Teams email body,
 * etc.). Provides a formatting toolbar with parity to LogicAppsUX's
 * RichTextToolbar:
 *   - undo / redo (with keyboard shortcuts)
 *   - block format (paragraph, h1–h3, bullet/number list, quote)
 *   - font family + size dropdowns
 *   - bold / italic / underline / strikethrough
 *   - text + highlight color pickers
 *   - insert / edit / remove link (Ctrl+K)
 *   - rich ↔ raw HTML toggle
 *
 * All HTML is sanitized via DOMPurify on both import and export to defend
 * against XSS.
 */

import {
  makeStyles,
  mergeClasses,
  tokens,
  shorthands,
  Label,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
  ToolbarToggleButton,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Input,
  Button,
  Field,
  Overflow,
  OverflowItem,
  useOverflowMenu,
  useIsOverflowItemVisible,
} from '@coreai/fluentui-react';
import {
  TextBold20Regular,
  TextItalic20Regular,
  TextUnderline20Regular,
  TextStrikethrough20Regular,
  TextBulletList20Regular,
  TextNumberListLtr20Regular,
  Link20Regular,
  LinkDismiss20Regular,
  Code20Regular,
  TextEditStyle20Regular,
  ArrowUndo20Regular,
  ArrowRedo20Regular,
  ChevronDown20Regular,
  TextFont20Regular,
  TextFontSize20Regular,
  Color20Regular,
  Highlight20Regular,
  TextHeader120Regular,
  TextHeader220Regular,
  TextHeader320Regular,
  TextParagraph20Regular,
  TextQuote20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
  type MutableRefObject,
} from 'react';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { isPrimaryMod } from '@/utils/input';
import {
  $applyNodeReplacement,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $setSelection,
  $isDecoratorNode,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $getNodeByKey,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  SKIP_DOM_SELECTION_TAG,
  TextNode,
  UNDO_COMMAND,
  type DOMConversion,
  type DOMConversionMap,
  type DOMConversionOutput,
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type RangeSelection,
  type SerializedTextNode,
  type TextFormatType,
} from 'lexical';
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListItemNode,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
  $isLinkNode,
  AutoLinkNode,
  LinkNode,
  TOGGLE_LINK_COMMAND,
} from '@lexical/link';
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  HeadingNode,
  QuoteNode,
  type HeadingTagType,
} from '@lexical/rich-text';
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
  $setBlocksType,
} from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';

import { ExpressionPreview } from './ExpressionPreview';
import { sanitizeHtml, sanitizeUrl, URL_ERROR_MESSAGE } from '@/utils/sanitization';
import { EndActionsSlot } from '../shared/EndActionsSlot';
import {
  HTML_EDITOR_DEFAULT_COLOR,
  HTML_EDITOR_PRESET_COLORS,
} from '@/data/htmlEditorColors';

// =============================================================================
// CONSTANTS
// =============================================================================

type FontProperty = 'font-family' | 'font-size';
type ColorProperty = 'color' | 'background-color';

const FONT_FAMILY_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['Arial', 'Arial'],
  ['Courier New', 'Courier New'],
  ['Georgia', 'Georgia'],
  ['Impact', 'Impact'],
  ['Tahoma', 'Tahoma'],
  ['Times New Roman', 'Times New Roman'],
  ['Trebuchet MS', 'Trebuchet MS'],
  ['Verdana', 'Verdana'],
];

const FONT_SIZE_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  '10', '11', '12', '13', '14', '15', '16', '18', '20', '24', '28', '32',
].map((px) => [`${px}px`, `${px}px`] as const);

const PRESET_COLORS = HTML_EDITOR_PRESET_COLORS;

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const DEFAULT_FONT_FAMILY = 'Arial';
const DEFAULT_FONT_SIZE = '15px';

const BLOCK_LABELS = {
  paragraph: 'Normal',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  bullet: 'Bulleted list',
  number: 'Numbered list',
  quote: 'Quote',
} as const;
type BlockType = keyof typeof BLOCK_LABELS;

const BLOCK_MENU: ReadonlyArray<{ value: BlockType; icon: ReactElement }> = [
  { value: 'paragraph', icon: <TextParagraph20Regular /> },
  { value: 'h1', icon: <TextHeader120Regular /> },
  { value: 'h2', icon: <TextHeader220Regular /> },
  { value: 'h3', icon: <TextHeader320Regular /> },
  { value: 'bullet', icon: <TextBulletList20Regular /> },
  { value: 'number', icon: <TextNumberListLtr20Regular /> },
  { value: 'quote', icon: <TextQuote20Regular /> },
];

const HEADING_TAGS: ReadonlySet<string> = new Set(['h1', 'h2', 'h3']);

interface InlineFormatButton {
  format: TextFormatType;
  label: string;
  shortcut?: string;
  icon: ReactElement;
}

const INLINE_FORMAT_BUTTONS: ReadonlyArray<InlineFormatButton> = [
  { format: 'bold', label: 'Bold', shortcut: 'Ctrl+B', icon: <TextBold20Regular /> },
  { format: 'italic', label: 'Italic', shortcut: 'Ctrl+I', icon: <TextItalic20Regular /> },
  { format: 'underline', label: 'Underline', shortcut: 'Ctrl+U', icon: <TextUnderline20Regular /> },
  { format: 'strikethrough', label: 'Strikethrough', icon: <TextStrikethrough20Regular /> },
];

// =============================================================================
// EDITOR HELPERS
// =============================================================================

/** Runs `fn` against the current range selection, if any, inside an editor update. */
function withRangeSelection(editor: LexicalEditor, fn: (selection: RangeSelection) => void): void {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) fn(selection);
  });
}

const applyTextStyle = (editor: LexicalEditor, property: FontProperty | ColorProperty, value: string): void =>
  withRangeSelection(editor, (selection) => $patchStyleText(selection, { [property]: value }));

function applyBlockType(editor: LexicalEditor, target: BlockType, current: BlockType): void {
  if (target === 'bullet') {
    editor.dispatchCommand(
      current === 'bullet' ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
      undefined
    );
    return;
  }
  if (target === 'number') {
    editor.dispatchCommand(
      current === 'number' ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
      undefined
    );
    return;
  }
  withRangeSelection(editor, (selection) => {
    if (target === 'paragraph') {
      $setBlocksType(selection, () => $createParagraphNode());
    } else if (target === 'quote') {
      $setBlocksType(selection, () => $createQuoteNode());
    } else {
      $setBlocksType(selection, () => $createHeadingNode(target as HeadingTagType));
    }
  });
}

// =============================================================================
// CUSTOM HTML IMPORT — preserve color/background/font on text nodes.
//
// Lexical's built-in <span> converter copies font-weight, font-style,
// text-decoration, and vertical-align onto child text nodes — but drops
// `color`, `background-color`, `font-family`, and `font-size`. We extend
// each TextNode importer so those properties from the parent element are
// merged into the resulting TextNode's style on import.
// =============================================================================

const PRESERVED_STYLE_PROPS: ReadonlyArray<readonly [domProp: string, cssProp: string]> = [
  ['color', 'color'],
  ['backgroundColor', 'background-color'],
  ['fontFamily', 'font-family'],
  ['fontSize', 'font-size'],
];

function mergeStyle(existing: string, additions: string): string {
  const trimmed = existing.trim();
  if (!trimmed) return additions;
  return trimmed.endsWith(';') ? `${trimmed} ${additions}` : `${trimmed}; ${additions}`;
}

function extractInlineStyle(node: Node): string {
  // `node.style` only exists on Elements (nodeType 1); accessing it on a
  // text node would throw and abort the entire HTML import.
  if (node.nodeType !== 1) return '';
  const style = (node as HTMLElement).style as unknown as Record<string, string>;
  const parts: string[] = [];
  for (const [domProp, cssProp] of PRESERVED_STYLE_PROPS) {
    const value = style[domProp];
    if (value) parts.push(`${cssProp}: ${value}`);
  }
  return parts.join('; ');
}

function preserveStyleOnConversion(parent: Node, output: DOMConversionOutput): DOMConversionOutput {
  const styleString = extractInlineStyle(parent);
  if (!styleString) return output;
  const inner = output.forChild;
  return {
    ...output,
    forChild: (lexicalNode: LexicalNode, parentNode) => {
      const next = inner ? inner(lexicalNode, parentNode) : lexicalNode;
      if (next && $isTextNode(next)) {
        next.setStyle(mergeStyle(next.getStyle(), styleString));
      }
      return next;
    },
  };
}

const PRESERVING_HTML_IMPORT_MAP: DOMConversionMap = (() => {
  const baseImporters = TextNode.importDOM();
  const map: DOMConversionMap = {};
  if (!baseImporters) return map;

  for (const [tag, getConverter] of Object.entries(baseImporters)) {
    const importer = getConverter as (node: Node) => DOMConversion | null;
    map[tag] = (node) => {
      const converter = importer(node);
      if (!converter) return null;
      const original = converter.conversion;
      return {
        ...converter,
        conversion: (domNode) => {
          const result = original(domNode);
          return result ? preserveStyleOnConversion(domNode, result) : result;
        },
      };
    };
  }
  return map;
})();

// =============================================================================
// LEXICAL CONFIG
// =============================================================================

const LEXICAL_THEME = {
  paragraph: 'editor-paragraph',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
  },
  list: {
    ul: 'editor-list-ul',
    ol: 'editor-list-ol',
    listitem: 'editor-listitem',
  },
  link: 'editor-link',
  quote: 'editor-quote',
};

// =============================================================================
// EXPRESSION HIGHLIGHTING
//
// Highlights `${...}` (JavaScript), `@{...}`, and `@func(...)` (Logic Apps)
// expression spans inside the editor with a subtle pill so users can see at
// a glance which text is dynamic. Implemented as a `TextNode` subclass plus
// a node transform — the same pattern Lexical uses for code-block syntax
// highlighting.
//
// The class wrapper is purely visual:
//  - `createDOM` adds an `editor-expression-token` class so styles apply
//    while editing.
//  - `exportDOM` emits a plain DOM Text node so the saved HTML stays clean
//    (no editor-only markup leaks into emails, Teams cards, etc.).
// =============================================================================

const EXPRESSION_TOKEN_CLASS = 'editor-expression-token';

interface ExpressionRange {
  start: number;
  end: number;
}

/**
 * Scan `text` for top-level expression spans and return their `[start, end)`
 * ranges. Recognizes:
 *   - `${...}` JavaScript expressions (with balanced braces & string literals)
 *   - `@{...}` Logic Apps inline expressions
 *   - `@func(...)` Logic Apps function calls
 * Skips escaped `$$` and any malformed span (returns no range for it).
 */
function findExpressionRanges(text: string): ExpressionRange[] {
  if (!text || text.length < 2) return [];
  const ranges: ExpressionRange[] = [];
  let i = 0;
  while (i < text.length) {
    const ch = text.charAt(i);
    const next = i + 1 < text.length ? text.charAt(i + 1) : '';

    // Escaped `$$` — skip, never starts an expression
    if (ch === '$' && next === '$') {
      i += 2;
      continue;
    }

    // `${...}` JavaScript expression
    if (ch === '$' && next === '{') {
      const end = findBalancedClose(text, i + 1, '{', '}');
      if (end > 0) {
        ranges.push({ start: i, end });
        i = end;
        continue;
      }
    }

    // `@{...}` Logic Apps inline expression
    if (ch === '@' && next === '{') {
      const end = findBalancedClose(text, i + 1, '{', '}');
      if (end > 0) {
        ranges.push({ start: i, end });
        i = end;
        continue;
      }
    }

    // `@func(...)` Logic Apps function call
    if (ch === '@' && /[a-zA-Z_]/.test(next)) {
      let j = i + 1;
      while (j < text.length && /[a-zA-Z0-9_.]/.test(text.charAt(j))) j++;
      if (j < text.length && text.charAt(j) === '(') {
        const end = findBalancedClose(text, j, '(', ')');
        if (end > 0) {
          ranges.push({ start: i, end });
          i = end;
          continue;
        }
      }
    }

    i++;
  }
  return ranges;
}

/**
 * Find the index just past the close-bracket that matches the open-bracket
 * at `text[openIdx]`. Two-pass:
 *
 *   1. *Strict* — honours `'…'`, `"…"`, and `` `…` `` string literals so
 *      brackets inside strings don't unbalance the count. Returns
 *      `-1` if the string never closes (e.g. an apostrophe in `can't`
 *      that swallowed the rest of the document).
 *   2. *Lenient* — when strict failed, retry ignoring string literals so a
 *      partially-typed expression still highlights once its brackets
 *      balance. Without this fallback users couldn't see their expression
 *      take shape until they had typed a matching quote far down the line,
 *      which Lexical's transform pass was hitting as an infinite-recursion
 *      precondition when text was edited in-place.
 *
 * Returns `-1` only when neither pass finds a balanced close.
 */
function findBalancedClose(text: string, openIdx: number, open: string, close: string): number {
  if (text.charAt(openIdx) !== open) return -1;
  const strict = findBalancedCloseScan(text, openIdx, open, close, true);
  return strict !== -1 ? strict : findBalancedCloseScan(text, openIdx, open, close, false);
}

function findBalancedCloseScan(
  text: string,
  openIdx: number,
  open: string,
  close: string,
  respectStrings: boolean
): number {
  let depth = 1;
  let pos = openIdx + 1;
  let inString = false;
  let stringChar = '';

  while (pos < text.length && depth > 0) {
    const c = text.charAt(pos);
    if (respectStrings && inString) {
      if (c === '\\' && pos + 1 < text.length) {
        pos += 2;
        continue;
      }
      if (c === stringChar) inString = false;
    } else if (respectStrings && (c === '"' || c === "'" || c === '`')) {
      inString = true;
      stringChar = c;
    } else if (c === open) {
      depth++;
    } else if (c === close) {
      depth--;
      if (depth === 0) return pos + 1;
    }
    pos++;
  }
  return -1;
}

/**
 * A `TextNode` whose DOM gets the highlight class. Subclassing (rather than
 * setting `setStyle` on a plain TextNode) keeps the editor-only styling out
 * of the exported HTML via the `exportDOM` override.
 */
class ExpressionTextNode extends TextNode {
  static override getType(): string {
    return 'expression-text';
  }

  static override clone(node: ExpressionTextNode): ExpressionTextNode {
    return new ExpressionTextNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    const existing = dom.className.trim();
    dom.className = existing ? `${existing} ${EXPRESSION_TOKEN_CLASS}` : EXPRESSION_TOKEN_CLASS;
    return dom;
  }

  override updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const isUpdated = super.updateDOM(prevNode, dom, config);
    if (!dom.classList.contains(EXPRESSION_TOKEN_CLASS)) {
      dom.classList.add(EXPRESSION_TOKEN_CLASS);
    }
    return isUpdated;
  }

  /** Emit the raw text on export so the highlight wrapper never leaks into saved HTML. */
  override exportDOM(): { element: Text } {
    return { element: document.createTextNode(this.__text) };
  }

  static override importJSON(serializedNode: SerializedTextNode): ExpressionTextNode {
    const node = $createExpressionTextNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  override exportJSON(): SerializedTextNode {
    return { ...super.exportJSON(), type: 'expression-text', version: 1 };
  }

  // Allow standard text-editing affordances (selection, deletion, format).
  override isSimpleText(): boolean {
    return true;
  }
}

function $createExpressionTextNode(text: string): ExpressionTextNode {
  return $applyNodeReplacement(new ExpressionTextNode(text));
}

function $isExpressionTextNode(node: LexicalNode | null | undefined): node is ExpressionTextNode {
  return node instanceof ExpressionTextNode;
}

const LEXICAL_NODES = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  LinkNode,
  AutoLinkNode,
  ExpressionTextNode,
];

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    rowGap: tokens.spacingVerticalXS,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
    minHeight: '20px',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  required: {
    color: tokens.colorPaletteRedForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    transitionProperty: 'border-color, box-shadow',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    },
    ':focus-within': {
      ...shorthands.border('1px', 'solid', tokens.colorBrandStroke1),
      boxShadow: `0 0 0 1px ${tokens.colorBrandStroke1}`,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    '@media (forced-colors: active)': {
      ...shorthands.border('1px', 'solid', 'CanvasText'),
      ':focus-within': {
        ...shorthands.border('2px', 'solid', 'Highlight'),
        boxShadow: 'none',
      },
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    paddingTop: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
    flexWrap: 'nowrap',
    minHeight: 'auto',
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
  },
  overflowItem: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  overflowMoreButton: {
    flexShrink: 0,
    minWidth: '28px',
    width: '28px',
    height: '24px',
    paddingLeft: 0,
    paddingRight: 0,
  },
  overflowPopoverSurface: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    rowGap: tokens.spacingVerticalXXS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    maxWidth: '320px',
  },
  labelRowEnd: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    flexShrink: 0,
    marginLeft: 'auto',
  },
  viewToggle: {
    minWidth: '28px',
    height: '24px',
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  dropdownButton: {
    minWidth: '0',
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalXS,
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXXS,
    height: '32px',
  },
  dropdownLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    maxWidth: '120px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdownChevron: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  menuItemActive: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  colorPickerSurface: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    minWidth: '224px',
  },
  colorSwatchGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 1fr)',
    gap: tokens.spacingHorizontalXS,
  },
  colorSwatch: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    padding: 0,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ':hover': {
      ...shorthands.border('1px', 'solid', tokens.colorBrandStroke1),
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '1px',
    },
  },
  colorSwatchActive: {
    ...shorthands.border('2px', 'solid', tokens.colorBrandStroke1),
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  colorPreview: {
    width: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusSmall,
    flexShrink: 0,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
  },
  popoverActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    columnGap: tokens.spacingHorizontalS,
  },
  linkSurface: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    minWidth: '280px',
  },
  editorContainer: {
    position: 'relative',
    minHeight: '160px',
    maxHeight: '400px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  contentEditable: {
    outline: 'none',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    minHeight: '160px',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground1,
    caretColor: tokens.colorNeutralForeground1,
    '& p': { margin: 0, marginBottom: tokens.spacingVerticalS },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': {
      marginLeft: tokens.spacingHorizontalL,
      marginTop: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    '& h1': {
      fontSize: tokens.fontSizeHero700,
      fontWeight: tokens.fontWeightSemibold,
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    '& h2': {
      fontSize: tokens.fontSizeBase600,
      fontWeight: tokens.fontWeightSemibold,
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    '& h3': {
      fontSize: tokens.fontSizeBase500,
      fontWeight: tokens.fontWeightSemibold,
      margin: 0,
      marginBottom: tokens.spacingVerticalS,
    },
    '& blockquote': {
      borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
      paddingLeft: tokens.spacingHorizontalM,
      marginLeft: 0,
      marginRight: 0,
      color: tokens.colorNeutralForeground2,
    },
    '& a': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'underline',
    },
    // Lexical wraps formatted text in <span class="editor-text-…"> rather than
    // semantic <strong>/<em>/etc.; style by class so combined formats render.
    '& strong, & .editor-text-bold': { fontWeight: tokens.fontWeightBold },
    '& em, & .editor-text-italic': { fontStyle: 'italic' },
    '& u, & .editor-text-underline': { textDecoration: 'underline' },
    '& s, & strike, & .editor-text-strikethrough': { textDecoration: 'line-through' },
    '& .editor-text-underline.editor-text-strikethrough': {
      textDecoration: 'underline line-through',
    },
    // Expression highlight — subtle brand-tinted pill so `${…}`, `@{…}` and
    // `@func(…)` spans visually pop without dominating the prose.
    [`& .${EXPRESSION_TOKEN_CLASS}`]: {
      backgroundColor: tokens.colorBrandBackground2,
      color: tokens.colorBrandForeground1,
      borderRadius: tokens.borderRadiusSmall,
      paddingLeft: tokens.spacingHorizontalXXS,
      paddingRight: tokens.spacingHorizontalXXS,
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase200,
      '@media (forced-colors: active)': {
        backgroundColor: 'Mark',
        color: 'MarkText',
        forcedColorAdjust: 'none',
      },
    },
  },
  placeholder: {
    position: 'absolute',
    top: tokens.spacingVerticalM,
    left: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    pointerEvents: 'none',
    userSelect: 'none',
  },
  rawHtmlEditor: {
    width: '100%',
    minHeight: '160px',
    maxHeight: '400px',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase300,
    border: 'none',
    outline: 'none',
    resize: 'vertical',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxSizing: 'border-box',
    '::placeholder': {
      color: tokens.colorNeutralForeground3,
    },
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
});

// =============================================================================
// SHARED DROPDOWN TRIGGER
// =============================================================================

interface DropdownTriggerProps {
  label: ReactNode;
  ariaLabel: string;
  disabled?: boolean;
  icon?: ReactElement;
}

// `MenuTrigger`/`PopoverTrigger` clone their single child to attach a `ref`,
// click handlers, and ARIA props — we must forward both ref and unknown props
// to the underlying ToolbarButton or the trigger appears inert.
const DropdownTrigger = forwardRef<HTMLButtonElement, DropdownTriggerProps>(
  ({ label, ariaLabel, disabled = false, icon, ...rest }, ref) => {
    const styles = useStyles();
    return (
      <ToolbarButton
        // Fluent's polymorphic ToolbarButton types `ref` as `Ref<never>`; the
        // runtime element is a button, so this cast is safe.
        ref={ref as React.Ref<never>}
        className={styles.dropdownButton}
        disabled={disabled}
        aria-label={ariaLabel}
        title={ariaLabel}
        // Spread `icon` only when defined to satisfy `exactOptionalPropertyTypes`.
        {...(icon ? { icon } : {})}
        {...rest}
      >
        <span className={styles.dropdownLabel}>{label}</span>
        <ChevronDown20Regular className={styles.dropdownChevron} />
      </ToolbarButton>
    );
  }
);
DropdownTrigger.displayName = 'DropdownTrigger';

// =============================================================================
// BLOCK FORMAT DROPDOWN
// =============================================================================

interface BlockFormatDropdownProps {
  blockType: BlockType;
  disabled: boolean;
}

function BlockFormatDropdown({ blockType, disabled }: BlockFormatDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const styles = useStyles();

  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>
        <DropdownTrigger
          label={BLOCK_LABELS[blockType]}
          ariaLabel="Text style"
          disabled={disabled}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {BLOCK_MENU.map(({ value, icon }) => (
            <MenuItem
              key={value}
              icon={icon}
              onClick={() => applyBlockType(editor, value, blockType)}
              className={value === blockType ? styles.menuItemActive : undefined}
            >
              {BLOCK_LABELS[value]}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

// =============================================================================
// FONT DROPDOWN (family + size)
// =============================================================================

interface FontDropdownProps {
  property: FontProperty;
  value: string;
  options: ReadonlyArray<readonly [string, string]>;
  ariaLabel: string;
  icon: ReactElement;
  disabled: boolean;
}

function FontDropdown({ property, value, options, ariaLabel, icon, disabled }: FontDropdownProps) {
  const [editor] = useLexicalComposerContext();
  const styles = useStyles();

  return (
    <Menu positioning="below-start">
      <MenuTrigger disableButtonEnhancement>
        <DropdownTrigger
          label={value || '(default)'}
          ariaLabel={ariaLabel}
          disabled={disabled}
          icon={icon}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {options.map(([optValue, label]) => (
            <MenuItem
              key={optValue}
              onClick={() => applyTextStyle(editor, property, optValue)}
              className={value === optValue ? styles.menuItemActive : undefined}
            >
              {label}
            </MenuItem>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

// =============================================================================
// COLOR PICKER
// =============================================================================

interface ColorPickerProps {
  ariaLabel: string;
  title: string;
  icon: ReactElement;
  color: string;
  onChange: (color: string) => void;
  onClear: () => void;
  disabled: boolean;
}

const FALLBACK_HEX = HTML_EDITOR_DEFAULT_COLOR;

function ColorPicker({
  ariaLabel, title, icon, color, onChange, onClear, disabled,
}: ColorPickerProps) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState<string>(color || FALLBACK_HEX);

  // Re-sync local input when the active selection's color changes externally.
  useEffect(() => {
    setHex(color || FALLBACK_HEX);
  }, [color]);

  const handleHexChange = useCallback(
    (next: string) => {
      setHex(next);
      if (HEX_COLOR_REGEX.test(next)) onChange(next);
    },
    [onChange]
  );

  const handlePreset = useCallback(
    (preset: string) => {
      setHex(preset);
      onChange(preset);
    },
    [onChange]
  );

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below">
      <PopoverTrigger disableButtonEnhancement>
        <ToolbarButton aria-label={ariaLabel} title={title} disabled={disabled} icon={icon} />
      </PopoverTrigger>
      <PopoverSurface className={styles.colorPickerSurface}>
        <div className={styles.colorRow}>
          <div
            className={styles.colorPreview}
            style={{ backgroundColor: hex || 'transparent' }}
            aria-hidden
          />
          <Field label="Hex">
            <Input
              size="small"
              value={hex}
              onChange={(_, data) => handleHexChange(data.value)}
              maxLength={7}
              spellCheck={false}
            />
          </Field>
        </div>
        <div className={styles.colorSwatchGrid} role="grid" aria-label={`${title} presets`}>
          {PRESET_COLORS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={mergeClasses(
                styles.colorSwatch,
                hex.toLowerCase() === preset.toLowerCase() && styles.colorSwatchActive
              )}
              style={{ backgroundColor: preset }}
              onClick={() => handlePreset(preset)}
              aria-label={`Color ${preset}`}
            />
          ))}
        </div>
        <div className={styles.popoverActions}>
          <Button
            size="small"
            appearance="subtle"
            onClick={() => {
              onClear();
              setOpen(false);
            }}
          >
            Clear
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

// =============================================================================
// LINK BUTTON
// =============================================================================

interface LinkButtonProps {
  isLink: boolean;
  currentUrl: string;
  disabled: boolean;
}

function LinkButton({ isLink, currentUrl, disabled }: LinkButtonProps) {
  const [editor] = useLexicalComposerContext();
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(currentUrl);
  const [error, setError] = useState<string | null>(null);

  // Reset local state when the popover (re)opens against a (possibly new) link.
  useEffect(() => {
    if (open) {
      setUrl(currentUrl);
      setError(null);
    }
  }, [open, currentUrl]);

  const handleApply = useCallback(() => {
    const sanitized = sanitizeUrl(url);
    if (!sanitized) {
      setError(URL_ERROR_MESSAGE);
      return;
    }
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitized);
    setOpen(false);
  }, [editor, url]);

  const handleRemove = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    setOpen(false);
  }, [editor]);

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open)} positioning="below" trapFocus>
      <PopoverTrigger disableButtonEnhancement>
        <ToolbarToggleButton
          name="format"
          value="link"
          aria-label={isLink ? 'Edit or remove link' : 'Insert link'}
          title={isLink ? 'Edit link (Ctrl+K)' : 'Insert link (Ctrl+K)'}
          disabled={disabled}
          icon={<Link20Regular />}
        />
      </PopoverTrigger>
      <PopoverSurface className={styles.linkSurface}>
        <Field
          label="Link URL"
          validationState={error ? 'error' : 'none'}
          {...(error ? { validationMessage: error } : {})}
        >
          <Input
            value={url}
            placeholder="https://example.com"
            onChange={(_, data) => {
              setUrl(data.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleApply();
              }
            }}
            autoFocus
          />
        </Field>
        <div className={styles.popoverActions}>
          {isLink && (
            <Button
              size="small"
              appearance="subtle"
              icon={<LinkDismiss20Regular />}
              onClick={handleRemove}
            >
              Remove
            </Button>
          )}
          <Button size="small" appearance="primary" onClick={handleApply}>
            {isLink ? 'Update' : 'Insert'}
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}

// =============================================================================
// TOOLBAR
// =============================================================================

interface FormatState {
  blockType: BlockType;
  fontFamily: string;
  fontSize: string;
  fontColor: string;
  bgColor: string;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  isLink: boolean;
  linkUrl: string;
}

const INITIAL_FORMAT_STATE: FormatState = {
  blockType: 'paragraph',
  fontFamily: DEFAULT_FONT_FAMILY,
  fontSize: DEFAULT_FONT_SIZE,
  fontColor: '',
  bgColor: '',
  isBold: false,
  isItalic: false,
  isUnderline: false,
  isStrikethrough: false,
  isLink: false,
  linkUrl: '',
};

/** Reads the current selection's formatting state. Must be called inside `editorState.read`. */
function readFormatState(selection: RangeSelection): FormatState {
  const anchorNode = selection.anchor.getNode();

  // Find the top-level block element containing the anchor.
  const element =
    anchorNode.getKey() === 'root'
      ? anchorNode
      : ($findMatchingParent(anchorNode, (n) => {
          const parent = n.getParent();
          return parent !== null && $isRootOrShadowRoot(parent);
        }) ?? anchorNode.getTopLevelElementOrThrow());

  let blockType: BlockType = 'paragraph';
  if ($isListNode(element)) {
    const list = $getNearestNodeOfType<ListNode>(anchorNode, ListNode) ?? element;
    blockType = list.getListType() === 'number' ? 'number' : 'bullet';
  } else if ($isHeadingNode(element)) {
    const tag = element.getTag();
    if (HEADING_TAGS.has(tag)) blockType = tag as BlockType;
  } else if (element.getType() === 'quote') {
    blockType = 'quote';
  }

  const parent = anchorNode.getParent();
  const linkNode = $isLinkNode(parent) ? parent : $isLinkNode(anchorNode) ? anchorNode : null;

  return {
    blockType,
    fontFamily: $getSelectionStyleValueForProperty(selection, 'font-family', DEFAULT_FONT_FAMILY),
    fontSize: $getSelectionStyleValueForProperty(selection, 'font-size', DEFAULT_FONT_SIZE),
    fontColor: $getSelectionStyleValueForProperty(selection, 'color', ''),
    bgColor: $getSelectionStyleValueForProperty(selection, 'background-color', ''),
    isBold: selection.hasFormat('bold'),
    isItalic: selection.hasFormat('italic'),
    isUnderline: selection.hasFormat('underline'),
    isStrikethrough: selection.hasFormat('strikethrough'),
    isLink: linkNode !== null,
    linkUrl: linkNode ? linkNode.getURL() : '',
  };
}

interface HtmlToolbarProps {
  disabled: boolean;
  isRawMode: boolean;
}

/** Toolbar item that renders as a real control inside (and optionally overflow). */
type OverflowItemEntry = { kind: 'item'; id: string; node: ReactNode };
/** Toolbar entry: either an item or a non-overflowing visual divider. */
type OverflowEntry = OverflowItemEntry | { kind: 'divider'; id: string };

function HtmlToolbar({ disabled, isRawMode }: HtmlToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const styles = useStyles();
  const [format, setFormat] = useState<FormatState>(INITIAL_FORMAT_STATE);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const refreshFormat = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) setFormat(readFormatState(selection));
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => editorState.read(refreshFormat)),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => { refreshFormat(); return false; },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => { setCanUndo(payload); return false; },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => { setCanRedo(payload); return false; },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        KEY_MODIFIER_COMMAND,
        (payload) => {
          const event = payload as KeyboardEvent;
          // ⌘K on Mac, Ctrl+K on Win/Linux — the standard "insert link"
          // chord across rich-text editors. Platform-gated so Ctrl+K on
          // Mac (which Lexical/emacs treats as kill-line) is not stolen.
          if (event.code === 'KeyK' && isPrimaryMod(event)) {
            event.preventDefault();
            const sanitized = sanitizeUrl('https://');
            if (sanitized) editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitized);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, refreshFormat]);

  const formattingDisabled = disabled || isRawMode;

  const checkedValues = useMemo<Record<string, string[]>>(() => {
    const active: string[] = [];
    if (format.isBold) active.push('bold');
    if (format.isItalic) active.push('italic');
    if (format.isUnderline) active.push('underline');
    if (format.isStrikethrough) active.push('strikethrough');
    if (format.isLink) active.push('link');
    return { format: active };
  }, [format.isBold, format.isItalic, format.isUnderline, format.isStrikethrough, format.isLink]);

  const dispatchFormat = useCallback(
    (formatType: TextFormatType) => editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType),
    [editor]
  );

  // Each entry is rendered twice — inline as an `<OverflowItem>` and again
  // inside the more-popover as a `<HiddenItemSlot>` — so we materialize the
  // toolbar as data and let the JSX below project both views from it.
  const entries: ReadonlyArray<OverflowEntry> = [
    {
      kind: 'item',
      id: 'undo',
      node: (
        <Tooltip content="Undo (Ctrl+Z)" relationship="label" withArrow>
          <ToolbarButton
            aria-label="Undo"
            icon={<ArrowUndo20Regular />}
            disabled={!canUndo || disabled}
            onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          />
        </Tooltip>
      ),
    },
    {
      kind: 'item',
      id: 'redo',
      node: (
        <Tooltip content="Redo (Ctrl+Y)" relationship="label" withArrow>
          <ToolbarButton
            aria-label="Redo"
            icon={<ArrowRedo20Regular />}
            disabled={!canRedo || disabled}
            onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          />
        </Tooltip>
      ),
    },
    { kind: 'divider', id: 'div-1' },
    {
      kind: 'item',
      id: 'block',
      node: <BlockFormatDropdown blockType={format.blockType} disabled={formattingDisabled} />,
    },
    {
      kind: 'item',
      id: 'font-family',
      node: (
        <FontDropdown
          property="font-family"
          value={format.fontFamily}
          options={FONT_FAMILY_OPTIONS}
          ariaLabel="Font family"
          icon={<TextFont20Regular />}
          disabled={formattingDisabled}
        />
      ),
    },
    {
      kind: 'item',
      id: 'font-size',
      node: (
        <FontDropdown
          property="font-size"
          value={format.fontSize}
          options={FONT_SIZE_OPTIONS}
          ariaLabel="Font size"
          icon={<TextFontSize20Regular />}
          disabled={formattingDisabled}
        />
      ),
    },
    { kind: 'divider', id: 'div-2' },
    ...INLINE_FORMAT_BUTTONS.map(({ format: fmt, label, shortcut, icon }) => ({
      kind: 'item' as const,
      id: `fmt-${fmt}`,
      node: (
        <Tooltip
          content={shortcut ? `${label} (${shortcut})` : label}
          relationship="label"
          withArrow
        >
          <ToolbarToggleButton
            name="format"
            value={fmt}
            aria-label={label}
            disabled={formattingDisabled}
            icon={icon}
            onClick={() => dispatchFormat(fmt)}
          />
        </Tooltip>
      ),
    })),
    {
      kind: 'item',
      id: 'color',
      node: (
        <ColorPicker
          ariaLabel="Text color"
          title="Text color"
          icon={<Color20Regular />}
          color={format.fontColor}
          onChange={(value) => applyTextStyle(editor, 'color', value)}
          onClear={() => applyTextStyle(editor, 'color', '')}
          disabled={formattingDisabled}
        />
      ),
    },
    {
      kind: 'item',
      id: 'highlight',
      node: (
        <ColorPicker
          ariaLabel="Highlight color"
          title="Highlight color"
          icon={<Highlight20Regular />}
          color={format.bgColor}
          onChange={(value) => applyTextStyle(editor, 'background-color', value)}
          onClear={() => applyTextStyle(editor, 'background-color', '')}
          disabled={formattingDisabled}
        />
      ),
    },
    {
      kind: 'item',
      id: 'link',
      node: <LinkButton isLink={format.isLink} currentUrl={format.linkUrl} disabled={formattingDisabled} />,
    },
  ];

  const overflowItems = entries.filter(
    (e): e is OverflowItemEntry => e.kind === 'item'
  );

  return (
    <Overflow padding={56}>
      <Toolbar
        className={styles.toolbar}
        aria-label="Formatting toolbar"
        size="small"
        checkedValues={checkedValues}
      >
        {entries.map((entry) =>
          entry.kind === 'divider' ? (
            <ToolbarDivider key={entry.id} />
          ) : (
            <OverflowItem key={entry.id} id={entry.id}>
              <span className={styles.overflowItem}>{entry.node}</span>
            </OverflowItem>
          )
        )}
        <ToolbarOverflowMenu items={overflowItems} />
      </Toolbar>
    </Overflow>
  );
}

interface ToolbarOverflowMenuProps {
  items: ReadonlyArray<OverflowItemEntry>;
}

function ToolbarOverflowMenu({ items }: ToolbarOverflowMenuProps) {
  const styles = useStyles();
  const { ref, isOverflowing, overflowCount } = useOverflowMenu<HTMLButtonElement>();
  if (!isOverflowing) return null;
  return (
    <Popover positioning="below-end" withArrow trapFocus>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          // PopoverTrigger's cloneElement narrows the child ref to `Ref<never>`;
          // `useOverflowMenu` hands us a real HTMLButtonElement ref. Cast.
          ref={ref as unknown as Ref<never>}
          appearance="subtle"
          size="small"
          aria-label={`More formatting actions, ${overflowCount} hidden`}
          title={`${overflowCount} more`}
          icon={<MoreHorizontal20Regular />}
          className={styles.overflowMoreButton}
        />
      </PopoverTrigger>
      <PopoverSurface className={styles.overflowPopoverSurface}>
        {items.map((item) => (
          <HiddenItemSlot key={item.id} id={item.id}>
            {item.node}
          </HiddenItemSlot>
        ))}
      </PopoverSurface>
    </Popover>
  );
}

interface HiddenItemSlotProps {
  id: string;
  children: ReactNode;
}

/** Renders `children` only when the matching `OverflowItem` is hidden by the overflow manager. */
function HiddenItemSlot({ id, children }: HiddenItemSlotProps) {
  const isVisible = useIsOverflowItemVisible(id);
  if (isVisible) return null;
  return <>{children}</>;
}

// =============================================================================
// LEXICAL PLUGINS
// =============================================================================

function $resetToEmpty(): void {
  const root = $getRoot();
  root.clear();
  root.append($createParagraphNode());
}

/**
 * `RootNode` only accepts `ElementNode`/`DecoratorNode` children. But
 * `$generateNodesFromDOM` can return bare inline/leaf nodes (most commonly a
 * plain `TextNode`) whenever the source HTML string has no top-level block
 * wrapper — e.g. a Logic Apps expression used as the *entire* field value
 * such as `${outputs('Compose')}` or `@triggerBody()`, which parses to a
 * `<body>` containing a single raw text node, not a `<p>`.
 *
 * Group any run of consecutive non-element/non-decorator nodes into a
 * `ParagraphNode` so `root.append(...)` never throws, instead of losing the
 * content to the catch-all error handler below.
 */
function $normalizeNodesForRoot(nodes: LexicalNode[]): LexicalNode[] {
  const normalized: LexicalNode[] = [];
  let currentParagraph: ReturnType<typeof $createParagraphNode> | null = null;

  for (const node of nodes) {
    if ($isElementNode(node) || $isDecoratorNode(node)) {
      currentParagraph = null;
      normalized.push(node);
    } else {
      if (!currentParagraph) {
        currentParagraph = $createParagraphNode();
        normalized.push(currentParagraph);
      }
      currentParagraph.append(node);
    }
  }

  return normalized;
}

/** Import on mount and when returning from raw mode, never on ordinary rich edits. */
function InitialContentPlugin({ htmlContent, mode }: { htmlContent: string; mode: 'rich' | 'raw' }) {
  const [editor] = useLexicalComposerContext();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (mode === 'raw') {
      initializedRef.current = false;
      return;
    }
    if (initializedRef.current) return;
    initializedRef.current = true;

    const trimmed = htmlContent?.trim();
    if (!trimmed) {
      editor.update($resetToEmpty, { tag: SKIP_DOM_SELECTION_TAG });
      return;
    }

    try {
      editor.update(() => {
        const dom = new DOMParser().parseFromString(sanitizeHtml(trimmed), 'text/html');
        const nodes = $normalizeNodesForRoot($generateNodesFromDOM(editor, dom));
        const root = $getRoot();
        root.clear();
        if (nodes.length === 0) {
          root.append($createParagraphNode());
        } else {
          root.append(...nodes);
        }
        $setSelection(null);
      }, { tag: SKIP_DOM_SELECTION_TAG });
    } catch (error) {
      // Last-resort fallback for genuinely unexpected failures (e.g. a
      // malformed node the normalization above doesn't anticipate). Common
      // "bare expression as the whole field value" cases are handled above
      // and should never reach this branch.
      console.error('Failed to initialize HTML content:', error);
      editor.update($resetToEmpty);
    }
  }, [editor, htmlContent, mode]);

  return null;
}

/** Emits sanitized HTML on every editor update. */
function OnChangeHtmlPlugin({ onChange, mode }: { onChange: (html: string) => void; mode: 'rich' | 'raw' }) {
  const [editor] = useLexicalComposerContext();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
      if (mode === 'raw' || (dirtyElements.size === 0 && dirtyLeaves.size === 0)) return;
      editorState.read(() => {
        onChangeRef.current(sanitizeHtml($generateHtmlFromNodes(editor)));
      });
    });
  }, [editor, mode]);

  return null;
}

function $isValidSavedSelection(selection: RangeSelection): boolean {
  return [selection.anchor, selection.focus].every((point) => {
    const node = $getNodeByKey(point.key);
    if (!node?.isAttached()) return false;
    const length = point.type === 'text' && $isTextNode(node)
      ? node.getTextContentSize()
      : point.type === 'element' && $isElementNode(node) ? node.getChildrenSize() : -1;
    return point.offset >= 0 && point.offset <= length;
  });
}

interface HtmlInsertionPluginProps {
  editorRef: MutableRefObject<LexicalEditor | null>;
  selectionRef: MutableRefObject<RangeSelection | null>;
  mode: 'rich' | 'raw';
  editable: boolean;
}

function HtmlInsertionPlugin({ editorRef, selectionRef, mode, editable }: HtmlInsertionPluginProps) {
  const [editor] = useLexicalComposerContext();
  useLayoutEffect(() => {
    editorRef.current = editor;
    return () => { editorRef.current = null; };
  }, [editor, editorRef]);
  useEffect(() => {
    editor.setEditable(editable && mode === 'rich');
  }, [editor, editable, mode]);
  useEffect(() => editor.registerUpdateListener(({ editorState }) => {
    if (mode !== 'rich' || !editor.getRootElement()?.contains(document.activeElement)) return;
    editorState.read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) selectionRef.current = selection.clone();
    });
  }), [editor, mode, selectionRef]);
  return null;
}

/**
 * Scan a text node for expression spans (`${…}`, `@{…}`, `@func(…)`) and
 * reconcile its type with what it should be:
 *
 *   - Plain `TextNode` whose text is a single complete expression → promote
 *     to `ExpressionTextNode` so it renders with the highlight pill.
 *   - `ExpressionTextNode` whose text is no longer a clean single expression
 *     → demote back to plain `TextNode`.
 *   - Plain `TextNode` mixing expression and non-expression text → split at
 *     expression boundaries and promote the matching segments.
 *
 * Returns `true` if it mutated, `false` if the node was already in its
 * stable configuration. Always copies `format`/`mode`/`style`/`detail` from
 * the source onto every replacement so bold/italic/coloured text survives
 * the round-trip and Lexical's built-in `normalizeTextNode` has nothing
 * left to reconcile.
 */
function $copyTextProperties(source: TextNode, target: TextNode): void {
  target.setFormat(source.getFormat());
  target.setDetail(source.getDetail());
  target.setMode(source.getMode());
  target.setStyle(source.getStyle());
}

function $reconcileExpressionHighlighting(node: TextNode): boolean {
  // Composition/IME and non-simple text nodes (URLs, code spans, …) are
  // left alone — Lexical owns those, and splitting them would corrupt the
  // user's input mid-keystroke.
  if (!node.isSimpleText()) return false;

  const text = node.getTextContent();
  const ranges = findExpressionRanges(text);
  const isExpr = $isExpressionTextNode(node);
  const isWholeExpression =
    ranges.length === 1 && ranges[0]!.start === 0 && ranges[0]!.end === text.length;

  if (isExpr && isWholeExpression) return false;
  if (!isExpr && ranges.length === 0) return false;

  if (!isExpr && isWholeExpression) {
    const promoted = $createExpressionTextNode(text);
    $copyTextProperties(node, promoted);
    node.replace(promoted);
    return true;
  }

  // ExpressionTextNode that no longer holds a clean expression → demote
  // first, then fall through so the demoted plain TextNode can be split if
  // it still contains embedded expressions. Doing both in one call (rather
  // than relying on a follow-up transform pass) is important because the
  // outer scheduler runs us with `skipTransforms: true` — there is no
  // follow-up pass.
  let working: TextNode = node;
  if (isExpr && !isWholeExpression) {
    const demoted = $createTextNode(text);
    $copyTextProperties(node, demoted);
    node.replace(demoted);
    working = demoted;
  }

  if (ranges.length === 0) return true;

  const offsets = new Set<number>();
  for (const { start, end } of ranges) {
    if (start > 0) offsets.add(start);
    if (end < text.length) offsets.add(end);
  }
  const ordered = Array.from(offsets).sort((a, b) => a - b);
  if (ordered.length === 0) return true;

  const parts = working.splitText(...ordered);
  let cursor = 0;
  for (const part of parts) {
    const partText = part.getTextContent();
    const partStart = cursor;
    const partEnd = cursor + partText.length;
    cursor = partEnd;
    const isPartExpression = ranges.some((r) => r.start === partStart && r.end === partEnd);
    if (isPartExpression) {
      const promoted = $createExpressionTextNode(partText);
      $copyTextProperties(part, promoted);
      part.replace(promoted);
    }
  }
  return true;
}

/**
 * Per-editor re-entry guard. The transform fires on a node, then schedules a
 * nested `editor.update(…, { skipTransforms: true })` to do the actual
 * mutation. Without this guard, transforms firing on TextNode AND
 * ExpressionTextNode for the same key would each schedule their own nested
 * update; with it, only the first wins until reconciliation completes.
 */
const nodesCurrentlyReconciling = new Set<string>();

const editorIds = new WeakMap<LexicalEditor, number>();
let nextEditorId = 1;

function getEditorId(editor: LexicalEditor): number {
  const existing = editorIds.get(editor);
  if (existing) return existing;
  const id = nextEditorId++;
  editorIds.set(editor, id);
  return id;
}

/**
 * The recursion-safe scheduler. Mirrors `@lexical/code`'s `codeNodeTransform`
 * approach:
 *
 *   1. A per-editor/per-key cache prevents duplicate work when both the
 *      `TextNode` and `ExpressionTextNode` transforms fire on the same key
 *      in the same pass.
 *   2. The actual reconciliation runs inside a nested
 *      `editor.update(…, { skipTransforms: true })` — the `skipTransforms`
 *      flag is what tells Lexical "the mutations I'm about to make should
 *      NOT queue more transforms." Without it, every `node.replace()` we
 *      do re-fires the transform on the replacement and we eventually trip
 *      Lexical's runaway-cycle detector with the "endless transform" error.
 */
function scheduleExpressionReconciliation(editor: LexicalEditor, nodeKey: NodeKey): void {
  const cacheKey = `${getEditorId(editor)}/${nodeKey}`;
  if (nodesCurrentlyReconciling.has(cacheKey)) return;
  nodesCurrentlyReconciling.add(cacheKey);

  editor.update(
    () => {
      const node = $getNodeByKey<TextNode>(nodeKey);
      if (!node || !node.isAttached() || !(node instanceof TextNode)) return;
      $reconcileExpressionHighlighting(node);
    },
    {
      onUpdate: () => {
        nodesCurrentlyReconciling.delete(cacheKey);
      },
      skipTransforms: true,
    }
  );
}

function registerExpressionHighlighting(editor: LexicalEditor): () => void {
  const handle = (node: TextNode) => {
    scheduleExpressionReconciliation(editor, node.getKey());
  };
  // Lexical keys transforms by exact class name, so register the same handler
  // on both the base TextNode and our subclass to cover every mutation path.
  return mergeRegister(
    editor.registerNodeTransform(TextNode, handle),
    editor.registerNodeTransform(ExpressionTextNode, handle)
  );
}

function ExpressionHighlightPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerExpressionHighlighting(editor), [editor]);
  return null;
}

// Test-only surface. Not part of the public API of this module; exposed so
// `HtmlField.test.tsx` can exercise the highlighting pipeline against a
// headless Lexical editor without rendering React.
export const __htmlFieldTestables = {
  ExpressionTextNode,
  LEXICAL_NODES,
  findExpressionRanges,
  registerExpressionHighlighting,
  $normalizeNodesForRoot,
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface HtmlFieldActions {
  insertText: (text: string) => void;
}

export interface HtmlFieldProps {
  /** Stable identifier (e.g. parameter name) used for the Lexical namespace. */
  name: string;
  /** Visible label rendered above the editor; also used as `aria-label`. */
  label?: string | undefined;
  /** Current HTML content. */
  value: string;
  /** Emits sanitized HTML whenever the user edits the content. */
  onChange: (html: string) => void;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
  required?: boolean | undefined;
  className?: string | undefined;
  /** Initial mode. Defaults to `'rich'`. */
  initialMode?: 'rich' | 'raw' | undefined;
  /** Action outputs from the last successful run, keyed by action name. Used to evaluate `${...}` / `@{...}` expressions in the preview. */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available for previews. */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since the last run (data may be stale). */
  isStale?: boolean | undefined;
  /**
   * Optional slot rendered on the right of the label row. Used to host
   * inline affordances like `<FieldActionChips />` so they sit alongside
   * the label rather than overlapping the editor toolbar.
   */
  endActions?: ReactNode | ((actions: HtmlFieldActions) => ReactNode);
  'data-testid'?: string | undefined;
}

export const HtmlField = ({
  name,
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
  readOnly = false,
  required = false,
  className,
  initialMode = 'rich',
  actionOutputs,
  hasRunData,
  isStale,
  endActions,
  'data-testid': testId,
}: HtmlFieldProps) => {
  const styles = useStyles();
  const [mode, setMode] = useState<'rich' | 'raw'>(initialMode);
  const lastEmittedHtmlRef = useRef(value);
  const editorRef = useRef<LexicalEditor | null>(null);
  const richSelectionRef = useRef<RangeSelection | null>(null);
  const rawRef = useRef<HTMLTextAreaElement>(null);
  const rawSelectionRef = useRef<{ start: number; end: number; value: string } | null>(null);
  const focusFrameRef = useRef<number | undefined>(undefined);

  useEffect(() => () => {
    if (focusFrameRef.current !== undefined) cancelAnimationFrame(focusFrameRef.current);
  }, []);

  const rememberRawSelection = useCallback((element: HTMLTextAreaElement) => {
    rawSelectionRef.current = {
      start: element.selectionStart,
      end: element.selectionEnd,
      value: element.value,
    };
  }, []);

  const rememberRichSelection = useCallback(() => {
    editorRef.current?.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) richSelectionRef.current = selection.clone();
    });
  }, []);

  const handleHtmlChange = useCallback(
    (html: string) => {
      if (html === lastEmittedHtmlRef.current) return;
      lastEmittedHtmlRef.current = html;
      onChange(html);
    },
    [onChange]
  );

  const handleRawChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      rememberRawSelection(event.target);
      lastEmittedHtmlRef.current = event.target.value;
      onChange(event.target.value);
    },
    [onChange, rememberRawSelection]
  );

  const insertText = useCallback((text: string) => {
    if (disabled || readOnly) return;
    if (focusFrameRef.current !== undefined) cancelAnimationFrame(focusFrameRef.current);
    if (mode === 'raw') {
      const saved = rawSelectionRef.current;
      const valid = saved?.value === value && saved.start <= saved.end && saved.end <= value.length;
      const start = valid ? saved.start : value.length;
      const end = valid ? saved.end : value.length;
      const next = value.slice(0, start) + text + value.slice(end);
      const caret = start + text.length;
      rawSelectionRef.current = { start: caret, end: caret, value: next };
      lastEmittedHtmlRef.current = next;
      onChange(next);
      // Run after the controlled value and the menu's return-focus have committed.
      focusFrameRef.current = requestAnimationFrame(() => {
        rawRef.current?.focus();
        rawRef.current?.setSelectionRange(caret, caret);
      });
      return;
    }
    const editor = editorRef.current;
    if (!editor) throw new Error('HTML editor is not ready for insertion');
    editor.update(() => {
      const saved = richSelectionRef.current;
      const selection = saved && $isValidSavedSelection(saved)
        ? saved.clone()
        : $getRoot().selectEnd();
      $setSelection(selection);
      selection.insertText(text);
      const nextSelection = $getSelection();
      richSelectionRef.current = $isRangeSelection(nextSelection) ? nextSelection.clone() : null;
    }, { discrete: true });
    focusFrameRef.current = requestAnimationFrame(() => editor.focus());
  }, [disabled, readOnly, mode, value, onChange]);

  const initialConfig = useMemo(
    () => ({
      namespace: `html-field-${name}`,
      theme: LEXICAL_THEME,
      nodes: LEXICAL_NODES,
      html: { import: PRESERVING_HTML_IMPORT_MAP },
      onError: (error: Error) => console.error('Lexical error:', error),
      editable: !disabled && !readOnly,
    }),
    [name, disabled, readOnly]
  );

  const toggleMode = useCallback(() => {
    richSelectionRef.current = null;
    rawSelectionRef.current = null;
    setMode((prev) => (prev === 'rich' ? 'raw' : 'rich'));
  }, []);

  return (
    <div className={styles.wrapper} data-testid={testId}>
      <div className={styles.labelRow}>
        {label ? (
          <Label className={styles.label} htmlFor={name}>
            {label}
            {required && (
              <span className={styles.required} aria-hidden="true">
                *
              </span>
            )}
          </Label>
        ) : (
          <span />
        )}
        <span className={styles.labelRowEnd}>
          <Tooltip
            content={mode === 'raw' ? 'Switch to rich text view' : 'Switch to HTML view'}
            relationship="label"
            withArrow
          >
            <Button
              type="button"
              appearance="subtle"
              size="small"
              aria-label="Toggle HTML view"
              aria-pressed={mode === 'raw'}
              disabled={disabled || readOnly}
              icon={mode === 'raw' ? <TextEditStyle20Regular /> : <Code20Regular />}
              onClick={toggleMode}
              className={styles.viewToggle}
            />
          </Tooltip>
          {endActions && (
            <EndActionsSlot>{typeof endActions === 'function' ? endActions({ insertText }) : endActions}</EndActionsSlot>
          )}
        </span>
      </div>
      <div
        className={mergeClasses(styles.container, disabled && styles.disabled, className)}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <HtmlToolbar
            disabled={disabled || readOnly}
            isRawMode={mode === 'raw'}
          />

          {mode === 'rich' ? (
            <div className={styles.editorContainer}>
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    id={name}
                    className={styles.contentEditable}
                    aria-label={label ?? name}
                    aria-required={required}
                    onBlur={rememberRichSelection}
                  />
                }
                placeholder={
                  <div className={styles.placeholder}>{placeholder ?? 'Enter rich text...'}</div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </div>
          ) : (
            <textarea
              ref={rawRef}
              id={name}
              className={styles.rawHtmlEditor}
              value={value}
              onChange={handleRawChange}
              onSelect={(event) => rememberRawSelection(event.currentTarget)}
              onBlur={(event) => rememberRawSelection(event.currentTarget)}
              disabled={disabled || readOnly}
              required={required}
              aria-required={required}
              placeholder={placeholder ?? 'Enter HTML...'}
              aria-label={label ?? name}
            />
          )}

          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <ExpressionHighlightPlugin />
          <HtmlInsertionPlugin editorRef={editorRef} selectionRef={richSelectionRef} mode={mode} editable={!disabled && !readOnly} />
          <InitialContentPlugin htmlContent={value} mode={mode} />
          <OnChangeHtmlPlugin onChange={handleHtmlChange} mode={mode} />
        </LexicalComposer>
      </div>

      {/* Expression preview — evaluates `${...}` (JavaScript) and `@{...}` /
          `@func()` (Logic Apps) expressions inside the HTML body, and
          renders an HTML preview of the result. Self-hides on empty input. */}
      <ExpressionPreview
        value={value}
        actionOutputs={actionOutputs}
        hasRunData={hasRunData}
        isStale={isStale}
      />
    </div>
  );
};

HtmlField.displayName = 'HtmlField';
